import { RecordProblem } from "@/components/patterns";
import { useCallRecord } from "@/components/calling/CallLink";
import React, { useEffect, useState, useCallback } from "react";
import { useRecordCrumb } from "@/components/shell/ShellContext";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, unwrapObject } from "@/lib/api";
import { useToast } from "@/context/ToastContext";
import { useAuth } from "@/context/AuthContext";
import { FormSkeleton } from "@/components/ui/page-skeletons";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { WhatsAppChatDialog } from "@/components/whatsapp/WhatsAppChatDialog";
import { TaskDialog } from "@/components/tasks/TaskDialog";
import { StatusTabs } from "@/components/patterns";
import { ActivityLog } from "@/components/customer/ActivityLog";
import { DealsWidget } from "@/components/entity-detail/DealsWidget";
import { QuotesWidget } from "@/components/entity-detail/QuotesWidget";
import { ProposalsWidget } from "@/components/entity-detail/ProposalsWidget";
import { EmailsWidget } from "@/components/entity-detail/EmailsWidget";
import { WhatsAppWidget } from "@/components/entity-detail/WhatsAppWidget";
import { ActivityTimeline } from "@/components/entity-detail/ActivityTimeline";
import { ContactRecordHeader } from "./record/ContactRecordHeader";
import { ContactProperties } from "./record/ContactProperties";
import { ContactRelated } from "./record/ContactRelated";
import type { Contact360 } from "@/types";
import type { Task } from "@/types/task";
import type { WhatsAppThread } from "@/types/whatsapp";

export function ContactDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { success, error: toastError } = useToast();
  const { hasPermission } = useAuth();
  const callRecord = useCallRecord();

  const [data, setData] = useState<Contact360 | null>(null);
  useRecordCrumb(data ? `${data.contact.first_name} ${data.contact.last_name || ""}`.trim() : undefined);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("activity");

  // Edit dialog state
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    mobile: "",
    job_title: "",
    is_primary: false,
  });
  const [editLoading, setEditLoading] = useState(false);

  // WhatsApp chat dialog
  const [waDialogOpen, setWaDialogOpen] = useState(false);

  // Task dialog
  const [taskDialogOpen, setTaskDialogOpen] = useState(false);

  const [problem, setProblem] = useState<unknown>(null);
  const fetch360 = useCallback(async () => {
    if (!id) return;
    try {
      setLoading(true);
      setProblem(null);
      const res = await api.get(`/api/Contact/360/${id}`);
      const payload = unwrapObject<Contact360>(res);
      setData(payload);
      if (payload?.contact) {
        setEditForm({
          first_name: payload.contact.first_name || "",
          last_name: payload.contact.last_name || "",
          email: payload.contact.email || "",
          phone: payload.contact.phone || "",
          mobile: payload.contact.mobile || "",
          job_title: payload.contact.job_title || "",
          is_primary: Boolean(payload.contact.is_primary),
        });
      }
    } catch (err: any) {
      setProblem(err);
    } finally {
      setLoading(false);
    }
  }, [id, navigate, toastError]);

  useEffect(() => {
    fetch360();
  }, [fetch360]);

  if (problem) return <RecordProblem error={problem} what="contact" record={{ type: "CONTACT", id }} backTo="/contacts" backLabel="Back to contacts" onRetry={fetch360} />;

  if (loading || !data) {
    return (
      <div className="p-6 space-y-6">
        <FormSkeleton />
      </div>
    );
  }

  const { contact, account, deals, quotations, emails, whatsapp, tasks } = data;
  const proposals = data.proposals || [];
  const history = data.history || [];
  const fullName = `${contact.first_name} ${contact.last_name || ""}`.trim();
  const phoneNum = contact.mobile || contact.phone;

  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id) return;
    try {
      setEditLoading(true);
      await api.put(`/api/Contact/Update/${id}`, {
        ...editForm,
        account_id: contact.account_id,
      });
      success("Contact updated", "Contact details saved successfully.");
      setEditOpen(false);
      fetch360();
    } catch (err: any) {
      toastError("Update failed", err.message || "Could not update contact.");
    } finally {
      setEditLoading(false);
    }
  };

  const doneTask = async (t: Task) => {
    try {
      await api.patch(`/api/Task/Complete/${t.id}`, { outcome: "Completed from the contact" });
      fetch360();
    } catch (err: any) {
      toastError("Could not complete the task", err.message || "Try again");
    }
  };

  const openChat = (_t: WhatsAppThread) => setWaDialogOpen(true);
  const timeline = (
    <ActivityTimeline deals={deals} quotations={quotations} emails={emails} whatsapp={whatsapp} tasks={tasks} proposals={proposals} history={history} onOpenWhatsApp={openChat} />
  );
  const tabItems = [
    { key: "activity", label: "Activity" },
    { key: "timeline", label: "Timeline" },
    { key: "deals", label: "Deals", count: deals.length },
    { key: "quotes", label: "Quotes", count: quotations.length },
    { key: "proposals", label: "Proposals", count: proposals.length },
    { key: "emails", label: "Emails", count: emails.length },
    { key: "whatsapp", label: "WhatsApp", count: whatsapp.length },
  ];
  const ownerName = contact.owner ? `${contact.owner.first_name} ${contact.owner.last_name || ""}`.trim() : "";

  return (
    <>
      <div className="flex flex-col gap-3">
        <ContactRecordHeader
          data={data}
          name={fullName}
          can={{ update: hasPermission("contact.update"), whatsapp: hasPermission("whatsapp.send") }}
          onCall={() => callRecord("CONTACT", contact.id, phoneNum)}
          onWhatsApp={() => setWaDialogOpen(true)}
          onEmail={() => window.open(`mailto:${contact.email}`)}
          onEdit={() => setEditOpen(true)}
        />

        <div className="grid grid-cols-1 items-start gap-3 @4xl/main:grid-cols-12">
          <div className="order-2 min-w-0 @4xl/main:order-1 @4xl/main:col-span-4 @6xl/main:col-span-3">
            <ContactProperties contact={contact} ownerName={ownerName} />
          </div>
          <div className="order-1 flex min-w-0 flex-col gap-3 @4xl/main:order-2 @4xl/main:col-span-8 @6xl/main:col-span-6">
            <StatusTabs tabs={tabItems} active={activeTab} onChange={setActiveTab} label="Contact sections" />
            {activeTab === "activity" && <ActivityLog relatedType="CONTACT" relatedId={contact.id} canCreate={hasPermission("activity.create")} />}
            {activeTab === "timeline" && timeline}
            {activeTab === "deals" && <DealsWidget deals={deals} canCreate={false} />}
            {activeTab === "quotes" && <QuotesWidget quotes={quotations} canCreate={hasPermission("quote.create")} />}
            {activeTab === "proposals" && <ProposalsWidget proposals={proposals} />}
            {activeTab === "emails" && <EmailsWidget emails={emails} canSend={hasPermission("email.send")} />}
            {activeTab === "whatsapp" && (
              <WhatsAppWidget
                threads={whatsapp}
                onOpenChat={openChat}
                onStartChat={phoneNum ? () => setWaDialogOpen(true) : undefined}
                canSend={hasPermission("whatsapp.send")}
              />
            )}
          </div>
          <div className="order-3 min-w-0 @4xl/main:col-span-12 @6xl/main:col-span-3">
            <ContactRelated
              contactId={contact.id}
              account={account}
              rollup={data.rollup}
              deals={deals}
              tasks={tasks}
              colleagues={data.colleagues || []}
              can={{ task: hasPermission("activity.create"), merge: hasPermission("contact.merge") }}
              onTask={() => setTaskDialogOpen(true)}
              onDoneTask={doneTask}
              onMerged={fetch360}
            />
          </div>
        </div>
      </div>

      {/* Edit Contact Dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="sm:max-w-lg">
          <form onSubmit={handleEditSubmit}>
            <DialogHeader>
              <DialogTitle>Edit Contact</DialogTitle>
              <DialogDescription>
                Update contact profile, phone numbers, and title.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-3.5 py-4 text-xs sm:text-sm">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label htmlFor="edit_first">First Name *</Label>
                  <Input
                    id="edit_first"
                    value={editForm.first_name}
                    onChange={(e) => setEditForm((p) => ({ ...p, first_name: e.target.value }))}
                    required
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="edit_last">Last Name</Label>
                  <Input
                    id="edit_last"
                    value={editForm.last_name}
                    onChange={(e) => setEditForm((p) => ({ ...p, last_name: e.target.value }))}
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="edit_title">Job Title / Designation</Label>
                <Input
                  id="edit_title"
                  value={editForm.job_title}
                  onChange={(e) => setEditForm((p) => ({ ...p, job_title: e.target.value }))}
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label htmlFor="edit_email">Email</Label>
                  <Input
                    id="edit_email"
                    type="email"
                    value={editForm.email}
                    onChange={(e) => setEditForm((p) => ({ ...p, email: e.target.value }))}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="edit_mobile">Mobile / WhatsApp</Label>
                  <Input
                    id="edit_mobile"
                    value={editForm.mobile}
                    onChange={(e) => setEditForm((p) => ({ ...p, mobile: e.target.value }))}
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="edit_phone">Office Phone</Label>
                <Input
                  id="edit_phone"
                  value={editForm.phone}
                  onChange={(e) => setEditForm((p) => ({ ...p, phone: e.target.value }))}
                />
              </div>

              <div className="flex items-center gap-2 pt-2">
                <input
                  type="checkbox"
                  id="edit_primary"
                  checked={editForm.is_primary}
                  onChange={(e) => setEditForm((p) => ({ ...p, is_primary: e.target.checked }))}
                  className="rounded border-input text-primary focus:ring-primary size-4"
                />
                <Label htmlFor="edit_primary" className="cursor-pointer text-xs font-medium">
                  Primary Contact for this Account
                </Label>
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setEditOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={editLoading}>
                {editLoading ? "Saving..." : "Save Changes"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* WhatsApp Chat Dialog */}
      {phoneNum && (
        <WhatsAppChatDialog
          open={waDialogOpen}
          onOpenChange={(v) => {
            setWaDialogOpen(v);
            if (!v) fetch360();
          }}
          phone={phoneNum}
          name={fullName}
          contactId={contact.id}
        />
      )}

      {/* Task Dialog */}
      <TaskDialog
        open={taskDialogOpen}
        onOpenChange={setTaskDialogOpen}
        defaultRelated={{
          type: "CONTACT",
          id: contact.id,
          name: fullName,
        }}
        onSaved={() => {
          setTaskDialogOpen(false);
          fetch360();
        }}
      />
    </>
  );
}
